/**
 * Licence handling for the app's APA house style.
 *
 * That house style is narrower than generic APA 7: the note under a figure
 * carries a Creative Commons licence or a public-domain statement and nothing
 * else. A plain copyright line — "© 2008 Disney", "Copyright 2008 by Disney" —
 * is deliberately left off the slide, so this module's job is to tell the two
 * apart and to hand back the deed URL a CC licence has to link to.
 */

import { cleanValue } from './apaShared';

export const LICENSE_KIND = {
  NONE: 'none',
  CC: 'cc',
  PUBLIC_DOMAIN: 'public-domain',
  COPYRIGHT: 'copyright'
};

// Longest first: "by-nc-sa" has to win over "by-nc" and "by".
const CC_CODES = ['by-nc-sa', 'by-nc-nd', 'by-nc', 'by-sa', 'by-nd', 'by'];

const CC_DEFAULT_VERSION = '4.0';

export const CC0_URL = 'https://creativecommons.org/publicdomain/zero/1.0/';
export const PUBLIC_DOMAIN_MARK_URL = 'https://creativecommons.org/publicdomain/mark/1.0/';

/** The canonical deed URL for a CC code and version, e.g. by-nc + 2.0. */
export const ccLicenseUrl = (code, version = CC_DEFAULT_VERSION) => {
  const normalizedCode = cleanValue(code).toLowerCase();
  if (!CC_CODES.includes(normalizedCode)) return '';
  const normalizedVersion = cleanValue(version) || CC_DEFAULT_VERSION;
  return `https://creativecommons.org/licenses/${normalizedCode}/${normalizedVersion}/`;
};

/** The deed URLs for the codes an author is most likely to type. */
export const CC_LICENSE_URLS = CC_CODES.reduce((urls, code) => {
  urls[`CC ${code.toUpperCase()}`] = ccLicenseUrl(code);
  return urls;
}, { CC0: CC0_URL });

const VERSION_RE = /\b(\d(?:\.\d)?)\b/;

// A licence written out in words rather than as a code.
const WORDED_CLAUSES = [
  { re: /attribution/i, part: 'by' },
  { re: /non[-\s]?commercial/i, part: 'nc' },
  { re: /share[-\s]?alike/i, part: 'sa' },
  { re: /no[-\s]?deriv(?:s|atives|ative works)?/i, part: 'nd' }
];

const codeFromWords = (value) => {
  const parts = WORDED_CLAUSES.filter((clause) => clause.re.test(value)).map((clause) => clause.part);
  if (!parts.includes('by')) return '';
  const code = parts.join('-');
  return CC_CODES.includes(code) ? code : '';
};

const codeFromShorthand = (value) => {
  const compact = value.toLowerCase().replace(/[\s_]+/g, '-');
  const match = /\bcc-?((?:by)(?:-(?:nc|sa|nd))*)/.exec(compact);
  if (!match) return '';
  const code = match[1].replace(/-+/g, '-');
  return CC_CODES.includes(code) ? code : '';
};

/**
 * What kind of licence a stored string is, and how it should read on a slide.
 *
 * Returns `{ kind, label, url, raw }`. `label` is the canonical spelling —
 * "CC BY-NC 2.0" whether the author typed that, "cc by-nc 2.0" or "Creative
 * Commons Attribution-NonCommercial 2.0". Only `cc` and `public-domain` may be
 * shown on a slide; `copyright` is dropped there under the house style.
 */
export const parseLicense = (rawLicense) => {
  const raw = cleanValue(rawLicense);
  if (!raw) return { kind: LICENSE_KIND.NONE, label: '', url: '', raw: '' };

  if (/\bcc0\b|creative\s+commons\s+zero/i.test(raw)) {
    return { kind: LICENSE_KIND.CC, label: 'CC0 1.0', url: CC0_URL, raw };
  }

  const code = codeFromShorthand(raw) || codeFromWords(raw);
  if (code) {
    const version = (VERSION_RE.exec(raw) || [])[1] || CC_DEFAULT_VERSION;
    return {
      kind: LICENSE_KIND.CC,
      label: `CC ${code.toUpperCase()} ${version}`,
      url: ccLicenseUrl(code, version),
      raw
    };
  }

  if (/public\s+domain\s+mark/i.test(raw)) {
    return { kind: LICENSE_KIND.PUBLIC_DOMAIN, label: raw, url: PUBLIC_DOMAIN_MARK_URL, raw };
  }
  if (/public\s+domain|no\s+known\s+copyright|^pd$/i.test(raw)) {
    return { kind: LICENSE_KIND.PUBLIC_DOMAIN, label: raw, url: '', raw };
  }

  return { kind: LICENSE_KIND.COPYRIGHT, label: raw, url: '', raw };
};

/** True when the house style lets this licence appear under the figure. */
export const isSlideDisplayableLicense = (rawLicense) => {
  const { kind } = parseLicense(rawLicense);
  return kind === LICENSE_KIND.CC || kind === LICENSE_KIND.PUBLIC_DOMAIN;
};

/**
 * The warning the citation editor shows for a licence that will not appear on
 * the slide, so an author who types "© 2008 Disney" learns why it vanished
 * rather than assuming the field is broken. Returns null when nothing is wrong.
 */
export const licenseWarning = (rawLicense) => {
  const { kind } = parseLicense(rawLicense);
  if (kind !== LICENSE_KIND.COPYRIGHT) return null;
  return 'This APA house style leaves plain copyright statements off the slide, so this will not appear in the note. '
    + 'Use a Creative Commons licence (CC BY, CC BY-NC 2.0, CC0 …) or a public domain statement, or leave the field empty.';
};

/** The licence values offered in the editor's picker. */
export const LICENSE_PRESETS = [
  { value: '', label: 'No licence' },
  ...CC_CODES.map((code) => ({
    value: `CC ${code.toUpperCase()} 4.0`,
    label: `CC ${code.toUpperCase()} 4.0`
  })),
  { value: 'CC0 1.0', label: 'CC0 1.0 (public domain dedication)' },
  { value: 'Public domain', label: 'Public domain' }
];
